/** The silent-start end's offered values, in seconds (D-5). */
export const SILENT_START_END_SECOND = [10, 20, 30, 60];
/** The longest conversation's offered values, in minutes (D-5). */
export const LONGEST_CONVERSATION_MINUTE = [5, 10, 15, 30, 60, 120];
/** The per-caller limit per clock hour, when on (D-5). */
export const PER_HOUR_RANGE = Object.freeze({ least: 1, most: 100 });
/** The per-caller limit per local day, when on (D-5). */
export const PER_DAY_RANGE = Object.freeze({ least: 1, most: 500 });
/** "No row" — every setting off; a named product value, written explicitly at the first save. */
export const PARTY_PROTECTION_OFF = Object.freeze({
    perHour: null,
    perDay: null,
    withheldPartyRefusal: false,
    silentStartEndSecond: null,
    longestConversationMinute: null,
});
/** The `detail.code` of a refused save (`400 validation_failed`, design §40.19.1), in refusal order. */
export const PARTY_PROTECTION_REFUSAL = [
    "per_hour_out_of_range",
    "per_day_out_of_range",
    "per_day_below_per_hour",
    "silent_start_not_offered",
    "longest_conversation_not_offered",
];
function inRange(value, range) {
    return Number.isSafeInteger(value) && value >= range.least && value <= range.most;
}
/**
 * The first rule a setting breaks, or `null` when it may be saved. The values are checked as numbers, not trusted as
 * their types: the api hands over what a request body carried, and a `silentStartEndSecond` of 15 must be refused here
 * whatever its static type said.
 */
export function partyProtectionRefusalOf(setting) {
    if (setting.perHour !== null && !inRange(setting.perHour, PER_HOUR_RANGE))
        return "per_hour_out_of_range";
    if (setting.perDay !== null && !inRange(setting.perDay, PER_DAY_RANGE))
        return "per_day_out_of_range";
    if (setting.perHour !== null && setting.perDay !== null && setting.perDay < setting.perHour)
        return "per_day_below_per_hour";
    if (setting.silentStartEndSecond !== null && !SILENT_START_END_SECOND.includes(setting.silentStartEndSecond)) {
        return "silent_start_not_offered";
    }
    if (setting.longestConversationMinute !== null &&
        !LONGEST_CONVERSATION_MINUTE.includes(setting.longestConversationMinute)) {
        return "longest_conversation_not_offered";
    }
    return null;
}
/** Whether a per-caller window is set — Stage B reserves only then (design §40.3.1 step (5)). */
export function perCallerLimitOn(setting) {
    return setting.perHour !== null || setting.perDay !== null;
}
/** Whether a VoiceAgent has saved any caller protection at all (the `party_protection.switch_off` log, §40.14.1). */
export function partyProtectionAnyOn(setting) {
    return (perCallerLimitOn(setting) ||
        setting.withheldPartyRefusal ||
        setting.silentStartEndSecond !== null ||
        setting.longestConversationMinute !== null);
}
// ---- blocked numbers (design §40.5, §40.19.2; D-6) ---------------------------------------------------------------------
/** Whom a blocked number is refused by: every VoiceAgent of the workspace, or one (`party_block_entry.scope`). */
export const PARTY_BLOCK_SCOPE = ["every_voice_agent", "one_voice_agent"];
/** How long a block lasts when it is not indefinite, in hours: a day, a week, thirty days (`expiry_hour`). */
export const PARTY_BLOCK_EXPIRY_HOUR = [24, 168, 720];
/** At most this many active blocked numbers per workspace (`422 blocked_number_limit_reached`). */
export const PARTY_BLOCK_ENTRY_ACTIVE_MOST = 5_000;
/** A blocked number's or an access entry's label, at most this many characters (0187's CHECK). */
export const PARTY_LABEL_CHARACTER_MOST = 80;
/** The `detail.code` of a refused block (`400 validation_failed`, design §40.19.2); `number_invalid` is the parser's. */
export const PARTY_BLOCK_ENTRY_REFUSAL = ["scope_voice_agent_mismatch", "label_too_long", "expiry_not_offered"];
/** Whether a label is over the limit. An empty label is a shape error the api refuses before this. */
export function partyLabelTooLong(label) {
    return label !== null && [...label].length > PARTY_LABEL_CHARACTER_MOST;
}
/**
 * The first rule a block breaks, or `null`. `one_voice_agent` names exactly one VoiceAgent and `every_voice_agent`
 * none (0187's `party_block_entry_scope`); the expiry is indefinite (`null`) or one of the offered spans.
 */
export function partyBlockEntryRefusalOf(input) {
    if ((input.scope === "one_voice_agent") !== (input.voiceAgentId !== null))
        return "scope_voice_agent_mismatch";
    if (partyLabelTooLong(input.label))
        return "label_too_long";
    if (input.expiryHour !== null && !PARTY_BLOCK_EXPIRY_HOUR.includes(input.expiryHour)) {
        return "expiry_not_offered";
    }
    return null;
}
//# sourceMappingURL=setting.js.map